import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "upload-wrap" }
const _hoisted_2 = {
  key: 0,
  class: "preview"
}
const _hoisted_3 = ["src"]
const _hoisted_4 = {
  key: 1,
  class: "camera"
}
const _hoisted_5 = {
  key: 2,
  class: "placeholder"
}
const _hoisted_6 = { class: "actions" }
const _hoisted_7 = { class: "file-name" }
const _hoisted_8 = {
  key: 0,
  class: "field-error"
}

import { computed, onBeforeUnmount, ref, watch } from "vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'SelfieForm',
  props: {
    selfie: { default: null },
    errors: { default: () => ({}) }
  },
  emits: ["update:selfie"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const selfieInput = ref<HTMLInputElement | null>(null);
const previewUrl = ref<string | null>(null);
const videoEl = ref<HTMLVideoElement | null>(null);
const canvasEl = ref<HTMLCanvasElement | null>(null);

const mediaStream = ref<MediaStream | null>(null);
const cameraUnavailable = ref(false);

const supportsCamera = computed(() => {
  return typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
});
const canUseCamera = computed(() => supportsCamera.value && !cameraUnavailable.value);
const isCameraOpen = computed(() => Boolean(mediaStream.value));
const primaryLabel = computed(() => {
  if (canUseCamera.value) {
    if (isCameraOpen.value) return "Capture";
    if (props.selfie) return "Retake Selfie";
    return "Take Selfie";
  }

  if (props.selfie) return "Change File";
  return "Select File";
});
const primaryIcon = computed(() => {
  if (isCameraOpen.value) return "mdi-camera";
  return canUseCamera.value ? "mdi-camera" : "mdi-upload";
});

function revokePreview() {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = null;
  }
}

function stopStream() {
  if (!mediaStream.value) return;
  mediaStream.value.getTracks().forEach((t) => t.stop());
  mediaStream.value = null;
}

function closeCamera() {
  stopStream();
}

watch(
  () => props.selfie,
  (file) => {
    revokePreview();
    if (file) {
      previewUrl.value = URL.createObjectURL(file);
    }
    if (file && isCameraOpen.value) {
      closeCamera();
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  revokePreview();
  closeCamera();
});

async function openCamera() {
  if (!canUseCamera.value) return false;
  if (isCameraOpen.value) return true;

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user" },
      audio: false,
    });
    mediaStream.value = stream;
    cameraUnavailable.value = false;

    if (videoEl.value) {
      videoEl.value.srcObject = stream;
      await videoEl.value.play().catch(() => undefined);
    }

    return true;
  } catch (error) {
    closeCamera();
    cameraUnavailable.value = true;
    return false;
  }
}

function pickUpload() {
  closeCamera();
  selfieInput.value?.click();
}

async function handlePrimaryAction() {
  if (canUseCamera.value) {
    if (isCameraOpen.value) {
      await captureFromCamera();
      return;
    }

    const opened = await openCamera();
    if (opened) return;
  }
  pickUpload();
}

function onChange(event: Event) {
  const input = event.target as HTMLInputElement | null;
  const file = input?.files?.[0] || null;
  emit("update:selfie", file);
  if (input) input.value = "";
}

async function captureFromCamera() {
  if (!videoEl.value || !canvasEl.value) return;

  const video = videoEl.value;
  const canvas = canvasEl.value;

  const width = video.videoWidth || 720;
  const height = video.videoHeight || 720;

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.drawImage(video, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9);
  });

  if (!blob) return;

  const file = new File([blob], `selfie-${Date.now()}.jpg`, {
    type: "image/jpeg",
  });

  emit("update:selfie", file);
  closeCamera();
}

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("input", {
        ref_key: "selfieInput",
        ref: selfieInput,
        type: "file",
        class: "file-input",
        accept: "image/*",
        capture: "user",
        onChange: onChange
      }, null, 544),
      _createElementVNode("div", {
        class: _normalizeClass(["upload-card", { 'has-error': __props.errors.selfie }])
      }, [
        (previewUrl.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("img", {
                src: previewUrl.value,
                alt: "Selfie preview"
              }, null, 8, _hoisted_3)
            ]))
          : (isCameraOpen.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createVNode(_component_v_btn, {
                  icon: "mdi-close",
                  size: "small",
                  variant: "tonal",
                  class: "camera-close",
                  onClick: closeCamera
                }),
                _createElementVNode("video", {
                  ref_key: "videoEl",
                  ref: videoEl,
                  autoplay: "",
                  playsinline: "",
                  muted: ""
                }, null, 512),
                _createElementVNode("canvas", {
                  ref_key: "canvasEl",
                  ref: canvasEl,
                  class: "camera-canvas"
                }, null, 512)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(_component_v_icon, { size: "22" }, {
                  default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                    _createTextVNode("mdi-camera-outline", -1)
                  ]))]),
                  _: 1
                }),
                _cache[1] || (_cache[1] = _createElementVNode("div", { class: "placeholder-text" }, "No selfie selected", -1))
              ])),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_component_v_btn, {
            variant: isCameraOpen.value ? 'flat' : 'outlined',
            color: isCameraOpen.value ? 'primary' : undefined,
            "prepend-icon": primaryIcon.value,
            onClick: handlePrimaryAction
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(primaryLabel.value), 1)
            ]),
            _: 1
          }, 8, ["variant", "color", "prepend-icon"])
        ]),
        _createElementVNode("div", _hoisted_7, _toDisplayString(__props.selfie?.name || "No file selected"), 1)
      ], 2),
      (__props.errors.selfie)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(__props.errors.selfie), 1))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})